import { motion } from "framer-motion";
import { ArrowRight, Building2, Handshake } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { useEarlyAccess } from "./EarlyAccessContext";

const audiences = [
  {
    id: "builders",
    role: "builder",
    icon: Building2,
    tag: "For Builders",
    title: "Built for Builders",
    body: "Expand your channel network, strengthen partner relationships, improve sales visibility, and unlock more opportunities to grow your projects.",
    points: [
      "Expand your channel network",
      "Strengthen partner relationships",
      "Improve sales visibility",
      "Unlock more opportunities",
    ],
    img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "partners",
    role: "channel_partner",
    icon: Handshake,
    tag: "For Channel Partners",
    title: "Built for Channel Partners",
    body: "Discover better opportunities, build stronger relationships with builders, work more efficiently, and grow your real estate business.",
    points: [
      "Discover better opportunities",
      "Stronger builder relationships",
      "Work more efficiently",
      "Grow your business",
    ],
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
  },
];

export default function Audience() {
  const { open } = useEarlyAccess();

  return (
    <section className="bg-[#f5f7fb] py-16 sm:py-20" data-testid="audience-section">
      <div className="mx-auto max-w-6xl px-2.5 sm:px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mb-10 max-w-2xl"
        >
          <p className="eyebrow text-[#3b82f6]">Two sides, one network</p>
          <h2 className="mt-4 text-3xl font-extrabold leading-[1.05] tracking-tight text-[#111827] sm:text-4xl">
            Purpose-built for both sides of the deal.
          </h2>
        </motion.div>

        <motion.div
          variants={stagger(0, 0.16)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-8 lg:grid-cols-2"
        >
          {audiences.map((a) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.id}
                id={a.id}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group flex flex-col overflow-hidden rounded-[28px] border border-[#e5e7eb] bg-white shadow-[0_20px_60px_-40px_rgba(30,58,138,0.5)]"
                data-testid={`audience-card-${a.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={a.img}
                    alt={a.title}
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/70 via-[#1e3a8a]/10 to-transparent" />
                  <div className="absolute left-6 top-6 flex h-10 w-10 items-center justify-center rounded-xl border border-white/30 bg-white/15 backdrop-blur-md">
                    <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="eyebrow absolute bottom-4 left-6 text-white/90">
                    {a.tag}
                  </p>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-xl font-extrabold tracking-tight text-[#111827]">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[#64748b]">
                    {a.body}
                  </p>

                  <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {a.points.map((p) => (
                      <li
                        key={p}
                        className="flex items-center gap-2.5 text-xs font-medium text-[#334155] sm:text-sm"
                      >
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#3b82f6]/10">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#1e3a8a]" />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => open(a.role)}
                    className="group/btn mt-6 flex items-center justify-center gap-2 rounded-full border border-[#1e3a8a]/20 bg-white px-5 py-2 text-[13px] font-semibold text-[#1e3a8a] transition-all hover:border-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white w-full sm:w-auto sm:self-start"
                    data-testid={`audience-cta-${a.id}`}
                  >
                    Get Early Access
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
